import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import DashboardLayout from "../../components/DashboardLayout";
import FormModal from "../../components/FormModal";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Edit, Trash2, Package } from "lucide-react";
import { useForm } from "@inertiajs/react";

interface Category {
  id: number;
  name: string;
  description: string;
  parent_id?: number;
  pdf_url?: string;
  created_at: string;
  updated_at: string;
}

interface CategoriesProps {
  categories: Category[];
  store_logo?: string;
  store_name?: string;
}

const Categories = ({ categories, store_logo, store_name }: CategoriesProps) => {
  const [isModalOpen, setModalOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);

   const {
     data,
     setData,
     post,
     put,
     delete: destroy,
     processing,
     errors,
     reset,
   } = useForm({
     name: "",
     description: "",
     pdf_url: "",
   });

  const openModal = (category: Category | null) => {
    setEditingCategory(category);
    if (category) {
      setData({
        name: category.name,
        description: category.description,
        pdf_url: category.pdf_url || "",
      });
    } else {
      reset();
    }
    setModalOpen(true);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (editingCategory) {
      put(`/admin/categories/${editingCategory.id}`, {
        onSuccess: () => setModalOpen(false),
      });
    } else {
      post("/admin/categories", {
        onSuccess: () => setModalOpen(false),
      });
    }
  };

  return (
    <DashboardLayout title="Gestión de Categorías" storeLogo={store_logo} storeName={store_name}>
      <div className="relative mb-8 p-8 rounded-2xl bg-gradient-to-r from-slate-400 via-slate-500 to-slate-600 shadow-xl overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-slate-300/20 via-slate-400/20 to-slate-500/20 animate-pulse" />
        <div className="absolute -top-4 -right-4 w-32 h-32 bg-white/10 rounded-full blur-xl" />
        <div className="absolute -bottom-4 -left-4 w-24 h-24 bg-white/10 rounded-full blur-xl" />
        <div className="relative z-10 flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold text-white mb-2">
              Gestión de Categorías
            </h1>
            <p className="text-slate-50 text-lg">
              Organiza tus productos en categorías
            </p>
          </div>
          <Button onClick={() => openModal(null)} className="bg-white text-slate-700 hover:bg-slate-50 shadow-lg hover:shadow-xl transition-all duration-200">
            <Plus className="mr-2 h-5 w-5" />
            Nueva Categoría
          </Button>
        </div>
      </div>

      <FormModal
        isOpen={isModalOpen}
        onClose={() => setModalOpen(false)}
        title={editingCategory ? "Editar Categoría" : "Nueva Categoría"}
        onSubmit={handleSubmit}
        isProcessing={processing}
      >
        <div className="grid w-full items-center gap-4">
          <div className="flex flex-col space-y-1.5">
            <Label htmlFor="name">Nombre</Label>
            <Input
              id="name"
              value={data.name}
              onChange={(e) => setData("name", e.target.value)}
            />
            {errors.name && <div className="text-red-500">{errors.name}</div>}
          </div>
           <div className="flex flex-col space-y-1.5">
             <Label htmlFor="description">Descripción</Label>
             <Textarea
               id="description"
               value={data.description}
               onChange={(e) => setData("description", e.target.value)}
             />
             {errors.description && (
               <div className="text-red-500">{errors.description}</div>
             )}
           </div>
           <div className="flex flex-col space-y-1.5">
             <Label htmlFor="pdf_url">URL del PDF</Label>
             <Input
               id="pdf_url"
               value={data.pdf_url}
               onChange={(e) => setData("pdf_url", e.target.value)}
               placeholder="https://ejemplo.com/catalogo.pdf"
             />
             {errors.pdf_url && <div className="text-red-500">{errors.pdf_url}</div>}
           </div>
        </div>
      </FormModal>

      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {categories.map((category, index) => (
          <Card key={category.id} className="relative overflow-hidden border-0 bg-gradient-to-br from-white to-slate-50 dark:from-slate-900 dark:to-slate-800 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 group">
            <div className={`absolute inset-0 bg-gradient-to-br opacity-0 group-hover:opacity-100 transition-opacity duration-300 ${
              index % 3 === 0 ? 'from-blue-500/5 via-cyan-500/5 to-teal-500/5' :
              index % 3 === 1 ? 'from-purple-500/5 via-pink-500/5 to-rose-500/5' :
              'from-emerald-500/5 via-green-500/5 to-lime-500/5'
            }`} />
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4 relative z-10">
              <CardTitle className="text-lg font-bold text-slate-800 dark:text-slate-200 group-hover:text-slate-900 dark:group-hover:text-slate-100 transition-colors">
                {category.name}
              </CardTitle>
              <div className={`p-2 rounded-lg shadow-sm ${
                index % 3 === 0 ? 'bg-gradient-to-r from-slate-500 to-slate-600' :
                index % 3 === 1 ? 'bg-gradient-to-r from-slate-500 to-slate-600' :
                'bg-gradient-to-r from-slate-500 to-slate-600'
              }`}>
                <Package className="h-5 w-5 text-white" />
              </div>
            </CardHeader>
              <CardContent className="relative z-10">
                <div className="text-base font-medium text-slate-600 dark:text-slate-400 mb-3 min-h-[3rem]">
                  {category.description || "Sin descripción"}
                </div>
                {category.pdf_url && (
                  <div className="mb-4">
                    <a
                      href={category.pdf_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-3 py-1 bg-gradient-to-r from-slate-600 to-slate-700 text-white text-sm rounded-lg hover:from-slate-700 hover:to-slate-800 shadow-sm hover:shadow-md transition-all duration-200"
                    >
                      <Package className="h-3 w-3" />
                      Ver PDF
                    </a>
                  </div>
                )}
                <div className="flex items-center justify-between">
                  <Badge variant="outline" className="border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-400">ID: {category.id}</Badge>
                 <div className="flex space-x-2">
                   <Button
                     variant="outline"
                     size="sm"
                     onClick={() => openModal(category)}
                     className="hover:bg-blue-50 dark:hover:bg-blue-900/20 border-blue-300 dark:border-blue-600 text-blue-600 dark:text-blue-400 hover:border-blue-400 dark:hover:border-blue-500 transition-all duration-200"
                   >
                     <Edit className="h-4 w-4" />
                   </Button>
                   <Button
                     variant="outline"
                     size="sm"
                     onClick={() =>
                       confirm("Are you sure?") &&
                       destroy(`/admin/categories/${category.id}`, {
                         method: "delete",
                       })
                     }
                     className="hover:bg-red-50 dark:hover:bg-red-900/20 border-red-300 dark:border-red-600 text-red-600 dark:text-red-400 hover:border-red-400 dark:hover:border-red-500 transition-all duration-200"
                   >
                     <Trash2 className="h-4 w-4" />
                   </Button>
                 </div>
               </div>
             </CardContent>
           </Card>
         ))}
       </div>

      {categories.length === 0 && (
        <Card className="border-0 shadow-xl bg-gradient-to-br from-white to-slate-50 dark:from-slate-900 dark:to-slate-800">
          <CardContent className="flex flex-col items-center justify-center py-16">
            <div className="p-4 rounded-full bg-gradient-to-r from-slate-100 to-slate-200 dark:from-slate-700 dark:to-slate-600 mb-6">
              <Package className="h-16 w-16 text-slate-400 dark:text-slate-500" />
            </div>
            <h3 className="text-2xl font-bold text-slate-800 dark:text-slate-200 mb-3">No hay categorías</h3>
            <p className="text-slate-600 dark:text-slate-400 text-center mb-6 max-w-md">
              Comienza agregando tu primera categoría de productos para organizar mejor tu tienda.
            </p>
            <Button onClick={() => openModal(null)} className="bg-gradient-to-r from-slate-600 to-slate-700 hover:from-slate-700 hover:to-slate-800 text-white shadow-lg hover:shadow-xl transition-all duration-200">
              <Plus className="mr-2 h-5 w-5" />
              Crear Primera Categoría
            </Button>
          </CardContent>
        </Card>
      )}
    </DashboardLayout>
  );
};

export default Categories;
